import { rewriteKey } from "../../fuxi/grade-rewrite.js";
import type { ItemWriting } from "./yufa-written-item.js";
import {
  grammarAnswer,
  teachingMatches,
  type GrammarTeaching,
} from "./yufa-teaching.js";
import { recordOf, writingsOf } from "./yufa-writing-read.js";

/** Fixed pieces give an error span the same identity in both scripts. */
export interface RepairBody {
  readonly kind: "repair";
  readonly pieces: readonly ItemWriting[];
  readonly diagnosis:
    | { readonly kind: "error"; readonly at: number }
    | { readonly kind: "no-change" };
  readonly misconception: string;
  /** The intended minimal correction comes first, followed by reviewed alternatives. */
  readonly accepted: readonly ItemWriting[];
}

/** Join the reviewed pieces without trimming or converting their punctuation. */
export function repairOriginal(item: Pick<RepairBody, "pieces">): ItemWriting {
  return {
    simplified: item.pieces.map((piece) => piece.simplified).join(""),
    traditional: item.pieces.map((piece) => piece.traditional).join(""),
  };
}

/** An unchanged key is allowed only by an explicit no-change diagnosis. */
export function repairBodyOf(value: unknown): RepairBody | undefined {
  const record = recordOf(value);
  const pieces = writingsOf(record?.["pieces"]);
  const accepted = writingsOf(record?.["accepted"]);
  const diagnosis = recordOf(record?.["diagnosis"]);
  const misconception = record?.["misconception"];
  if (
    record?.["kind"] !== "repair" ||
    pieces === undefined ||
    pieces.length < 2 ||
    pieces.length > 8 ||
    accepted === undefined ||
    !separate(accepted) ||
    typeof misconception !== "string" ||
    !misconception.trim() ||
    pieces.some(
      (piece) =>
        !rewriteKey(piece.simplified) || !rewriteKey(piece.traditional),
    )
  ) {
    return undefined;
  }
  const original = repairOriginal({ pieces });
  if (diagnosis?.["kind"] === "no-change") {
    return diagnosis["at"] === undefined &&
      accepted.length === 1 &&
      accepted[0]!.simplified === original.simplified &&
      accepted[0]!.traditional === original.traditional
      ? {
          kind: "repair",
          pieces,
          accepted,
          misconception,
          diagnosis: { kind: "no-change" },
        }
      : undefined;
  }
  const at = diagnosis?.["at"];
  return diagnosis?.["kind"] === "error" &&
    typeof at === "number" &&
    Number.isInteger(at) &&
    at >= 0 &&
    at < pieces.length &&
    separate([original, ...accepted]) &&
    minimalCorrection(pieces, at, accepted[0]!)
    ? {
        kind: "repair",
        pieces,
        accepted,
        misconception,
        diagnosis: { kind: "error", at },
      }
    : undefined;
}

function separate(forms: readonly ItemWriting[]): boolean {
  return (["simplified", "traditional"] as const).every((script) => {
    const keys = forms.map((form) => rewriteKey(form[script]));
    return !keys.includes("") && new Set(keys).size === forms.length;
  });
}

function minimalCorrection(
  pieces: readonly ItemWriting[],
  at: number,
  corrected: ItemWriting,
): boolean {
  return (["simplified", "traditional"] as const).every((script) => {
    const before = pieces
      .slice(0, at)
      .map((piece) => piece[script])
      .join("");
    const after = pieces
      .slice(at + 1)
      .map((piece) => piece[script])
      .join("");
    const original = pieces.map((piece) => piece[script]).join("");
    return (
      corrected[script].startsWith(before) &&
      corrected[script].endsWith(after) &&
      corrected[script].length >= before.length + after.length &&
      (corrected[script].match(/\p{P}/gu) ?? []).join("") ===
        (original.match(/\p{P}/gu) ?? []).join("")
    );
  });
}

/** The original and every correction need feedback approved with the complete question. */
export function repairTeachingMatches(
  item: RepairBody,
  teaching: GrammarTeaching | undefined,
): boolean {
  if (
    teaching?.task !== "repair" ||
    !teachingMatches(teaching, item.accepted)
  ) {
    return false;
  }
  const original = repairOriginal(item);
  const expected = item.diagnosis.kind === "no-change" ? "target" : "error";
  return [original.simplified, original.traditional].every(
    (text) => grammarAnswer(teaching, text)?.status === expected,
  );
}
